import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatPaginator, MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { MatSort, MatSortModule, Sort } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { BehaviorSubject, tap } from "rxjs";
import { GrilleTri } from "src/app/zzz/ui/grille/grille.modele";
import { PilotageService } from "../pilotage.service";
import { FormService } from "src/app/shared/services/form.service";
import { Utilisateur } from "../pilotage.models";
import { saveAs } from 'file-saver';
import { MessageService } from "@igo2/core/message";
import { LanguageService } from "@igo2/core/language";
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from "@angular/material/form-field";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";

interface RechercheDto {
  prenom?: string;
  nom?: string;
}

export interface TableState {
  sort?: GrilleTri;
  page: number;
  pageSize: number;
  pageLength: number;
  recherche?: RechercheDto;
}

@Component({
    selector: 'pilotage-users',
    templateUrl: './pilotage-users.component.html',
    styleUrls: ['./pilotage-users.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule
    ]
})
export class PilotageUsersComponent implements OnInit {
  #formBuilder = inject(FormBuilder);
  #formService = inject(FormService);
  #pilotageService = inject(PilotageService);
  #messageService = inject(MessageService);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  form = this.#formBuilder.group({
    prenom: [null],
    nom: [null]
  }, {
    validators: [this.#validerUnChampRempli.bind(this)]
  });

  displayedColumns: string[] = ['codeUtilIdz', 'nomComplet', 'telephone', 'nomDirectionOuEmployeur', 'adrCourriel'];
  defaultValue: Utilisateur[] = [];
  dataSource = new MatTableDataSource<Utilisateur>(this.defaultValue);
  tableState: TableState = {
    page: 1,
    pageSize: 25,
    pageLength: 0,
    sort: {
      colonne: 'nomComplet',
      order: 'asc'
    },
  };
  messagesDeValidation: { [key: string]: string } = {};
  isDownloading: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.messagesDeValidation.requiredAtLeastOne =
      this.languageService.translate.instant('messages.requiredAtLeastOne')
        .replace('{0}', this.languageService.translate.instant('journal.statistiqueConnexion.form.messagesPlaceholders.ph1UnParametre'));
  }

  reinitialiser(): void {
    this.form.reset();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.tableState.recherche = undefined;
    this.tableState.page = 1;
    this.tableState.pageSize = 25;
    this.tableState.pageLength = 0;
    this.dataSource.data = this.defaultValue;
  }

  rechercher(): void {
    this.form.markAsTouched();
    if (this.#formService.valider(this.form)) {
      this.tableState.recherche = this.form.value;
      this.tableState.page = 1;
      this.#chargerDonnees();
    }
  }

  #chargerDonnees(): void {
    var data = {
      nom: this.tableState.recherche?.nom,
      prenom: this.tableState.recherche?.prenom,
      criteres: {
        tris: this.tableState.sort && this.tableState.sort.order ? [
          new GrilleTri({ colonne: this.tableState.sort.colonne, order: this.tableState.sort.order })
        ] : [],
        skip: this.tableState.pageSize * this.tableState.page,
        page: this.tableState.page,
        take: this.tableState.pageSize,
        sort: null,
        count: 0,
        filtres: [],
        pages: null,
        recherche: null
      }
    };

    this.#pilotageService.obtenirUtilisateursGrille(data).pipe(
      tap(rep => {
        this.tableState = { ...this.tableState, ...{ pageLength: rep.criteres.count } };
        this.dataSource.data = rep.data;

        if (this.dataSource.data.length === 0) {
          this.#messageService.message({
            text: this.languageService.translate.instant('sigt.global.messageAucuneDonnee')
          });
        }
      })
    ).subscribe();
  }

  trier(event: Sort): void {
    if (event && event.direction && (this.tableState.sort === undefined || this.tableState.sort.order !== event.direction)) {
      this.tableState.sort = {
        order: event.direction,
        colonne: event.active
      };
    } else if (event === undefined || event.direction === '') {
      this.tableState.sort = undefined;
    }

    if (this.tableState.recherche) {
      this.#chargerDonnees();
    }
  }

  changerPage(event: PageEvent): void {
    this.tableState.page = event.pageIndex + 1;
    this.tableState.pageSize = event.pageSize;

    if (this.tableState.recherche) {
      this.#chargerDonnees();
    }
  }

  #validerUnChampRempli(controlGroup: FormGroup): { [key: string]: any } | null {
    const hasValue = controlGroup.controls["prenom"].value || controlGroup.controls["nom"].value ? true : false;
    return hasValue ? null : {
      requiredAtLeastOne: {
        message: this.messagesDeValidation?.requiredAtLeastOne
      }
    };
  }

  telecharger() {
    this.isDownloading.next(true);
    var data = {
      nom: this.tableState.recherche?.nom,
      prenom: this.tableState.recherche?.prenom
    };

    this.#pilotageService.exportUtilisateurs(data)
      .subscribe(rep => {
        if (rep) {
          saveAs(rep, 'Utilisateurs.csv');
          this.isDownloading.next(false);
        }
      });
  }
}
